import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useBreadcrumbLabels } from '@/contexts/BreadcrumbContext';
import { ROUTES } from '@/lib/constants';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs() {
  const location = useLocation();
  const { t, isRTL } = useI18n();
  const overrides = useBreadcrumbLabels();
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  const pathnames = location.pathname.split('/').filter((x) => x);

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath = '';

    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    pathnames.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathnames.length - 1;
      const isOpaqueId = !isNaN(Number(segment)) || UUID_RE.test(segment);

      // Page-supplied label takes priority — used to surface friendly names
      // (e.g. tracking_number) in place of opaque ids in the URL.
      const overrideLabel = overrides[segment];
      if (overrideLabel) {
        breadcrumbs.push({
          label: overrideLabel,
          href: isLast ? undefined : currentPath,
        });
        return;
      }

      if (isOpaqueId) {
        breadcrumbs.push({ label: t.breadcrumbs.details });
        return;
      }

      const label = t.breadcrumbs[segment] ?? segment;
      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  const lastCrumb = breadcrumbs[breadcrumbs.length - 1];

  return (
    <nav className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground overflow-hidden">
      {/* On mobile show only: Home > last crumb. On sm+ show full path. */}
      <Link
        to={ROUTES.DASHBOARD}
        className="flex shrink-0 items-center gap-1 hover:text-foreground transition-colors"
      >
        <Home className="h-4 w-4" />
      </Link>

      {/* sm+: full breadcrumb trail */}
      {breadcrumbs.slice(0, -1).map((crumb, index) => (
        <div key={index} className="hidden sm:flex items-center gap-1 shrink-0">
          <Chevron className="h-4 w-4" />
          {crumb.href ? (
            <Link to={crumb.href} className="hover:text-foreground transition-colors whitespace-nowrap">
              {crumb.label}
            </Link>
          ) : (
            <span className="whitespace-nowrap">{crumb.label}</span>
          )}
        </div>
      ))}

      {/* Last crumb always visible, truncated if needed */}
      {lastCrumb && (
        <div className="flex items-center gap-1 min-w-0">
          <Chevron className="h-4 w-4 shrink-0" />
          <span className="text-foreground font-medium truncate">
            {lastCrumb.label}
          </span>
        </div>
      )}
    </nav>
  );
}
